import React from 'react';
import type { BoxProps } from '@withjoy/joykit';
import { Box, Divider, Flex, TextV2 } from '@withjoy/joykit';

import { useTranslation } from '@shared/core';
import { InfoTooltip } from '@shared/components/InfoTooltip';

interface DiscountBadgeWithTooltipProps
  extends Readonly<{
    label: string;
    totalPrice: string;
    originalPrice: string;
    discountAmount: string;
    /** Styling for the badge itself, so a caller can restyle it without losing the breakdown. */
    badgeProps?: BoxProps;
  }> {}

interface DiscountTooltipContentProps
  extends Readonly<{
    totalPrice: string;
    originalPrice: string;
    discountAmount: string;
  }> {}

export const DiscountBadge = React.forwardRef<HTMLDivElement, Readonly<{ label: string }> & BoxProps>(({ label, ...rest }, ref) => (
  <Box
    ref={ref}
    paddingY={1}
    paddingX={3}
    color="white"
    borderRadius={2}
    whiteSpace="nowrap"
    typographyVariant="label2"
    backgroundColor="positive6"
    {...rest}
  >
    {label}
  </Box>
));

DiscountBadge.displayName = 'DiscountBadge';

const DiscountTooltipContent: React.FC<DiscountTooltipContentProps> = ({ originalPrice, discountAmount, totalPrice }) => {
  const { tq } = useTranslation('accommodationsConsole');
  return (
    <Flex
      gap={4}
      flexDirection="column"
    >
      <Flex justifyContent="space-between">
        <TextV2 typographyVariant="body1">{tq`Original nightly price`}</TextV2>
        <TextV2 typographyVariant="body1">{originalPrice}</TextV2>
      </Flex>
      <Flex justifyContent="space-between">
        <TextV2 typographyVariant="body1">{tq`Joy Discount`}</TextV2>
        <TextV2 typographyVariant="body1">{tq`-${discountAmount}`}</TextV2>
      </Flex>
      <TextV2 typographyVariant="label1">{tq`You're getting a reduced rate for Joy hosts and guests only.`}</TextV2>
      <Divider orientation="horizontal" />
      <Flex justifyContent="space-between">
        <TextV2 typographyVariant="hed4">{tq`Total`}</TextV2>
        <TextV2 typographyVariant="hed4">{totalPrice}</TextV2>
      </Flex>
    </Flex>
  );
};

export const DiscountBadgeWithTooltip: React.FC<DiscountBadgeWithTooltipProps> = ({ label, originalPrice, discountAmount, totalPrice, badgeProps }) => {
  const { tq } = useTranslation('accommodationsConsole');
  return (
    <InfoTooltip
      placement="bottom"
      tooltipOverrides={{
        Content: {
          props: {
            color: 'black',
            backgroundColor: 'white',
            boxShadow: '0px 7px 27px 0px rgba(44, 41, 37, 0.06), 0px 4px 16px 0px rgba(0, 0, 0, 0.07)',
          },
        },
        Arrow: {
          props: {
            backgroundColor: 'white',
          },
        },
      }}
      ariaLabel={tq`Discount price breakdown`}
      content={
        <DiscountTooltipContent
          totalPrice={totalPrice}
          originalPrice={originalPrice}
          discountAmount={discountAmount}
        />
      }
    >
      <DiscountBadge
        label={label}
        {...badgeProps}
      />
    </InfoTooltip>
  );
};
